import { useState } from 'react'
import { X, TextSelect, Plus, RefreshCw } from 'lucide-react'
import type { GemCriteriaTemplate } from '../../types/streams'

interface SaveAsTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  gemCriterion: {
    title: string
    description: string
  }
  templates: GemCriteriaTemplate[]
  onSaved: (template: GemCriteriaTemplate) => void
}

export function SaveAsTemplateModal({
  isOpen,
  onClose,
  gemCriterion,
  templates,
  onSaved,
}: SaveAsTemplateModalProps) {
  const [mode, setMode] = useState<'new' | 'overwrite'>('new')
  const [newTemplateName, setNewTemplateName] = useState(gemCriterion.title)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    templates.length > 0 ? templates[0].id : null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  if (!isOpen) return null

  const handleSave = async () => {
    setError(null)
    setIsSubmitting(true)

    try {
      if (mode === 'new') {
        const response = await fetch('/gem_criteria_templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            gem_criteria_template: {
              title: newTemplateName.trim(),
              description: gemCriterion.description,
            },
          }),
        })

        if (response.ok) {
          const data = await response.json()
          onSaved(data.template)
          onClose()
        } else {
          const data = await response.json()
          setError(data.errors?.join(', ') || 'Failed to create template')
        }
      } else if (selectedTemplateId) {
        const response = await fetch(`/gem_criteria_templates/${selectedTemplateId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            gem_criteria_template: {
              title: gemCriterion.title,
              description: gemCriterion.description,
            },
          }),
        })

        if (response.ok) {
          const data = await response.json()
          onSaved(data.template)
          onClose()
        } else {
          const data = await response.json()
          setError(data.errors?.join(', ') || 'Failed to update template')
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const canSave =
    mode === 'new' ? newTemplateName.trim().length > 0 : selectedTemplateId !== null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
              <TextSelect className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
              Save as Template
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Mode Selection */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('new')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                mode === 'new'
                  ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-700 dark:text-teal-300'
                  : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700'
              }`}
            >
              <Plus className="w-4 h-4" />
              Create New
            </button>
            <button
              onClick={() => setMode('overwrite')}
              disabled={templates.length === 0}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                mode === 'overwrite'
                  ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-700 dark:text-teal-300'
                  : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700'
              }`}
            >
              <RefreshCw className="w-4 h-4" />
              Overwrite Existing
            </button>
          </div>

          {mode === 'new' ? (
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                Template Name
              </label>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                placeholder="Enter template name"
              />
              <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                The description will be copied from the current gem criteria.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                Select Template to Overwrite
              </label>
              {templates.length === 0 ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  No existing templates. Create a new one instead.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {templates.map((template) => (
                    <label
                      key={template.id}
                      className={`block p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTemplateId === template.id
                          ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500'
                          : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="template"
                          value={template.id}
                          checked={selectedTemplateId === template.id}
                          onChange={() => setSelectedTemplateId(template.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-stone-900 dark:text-stone-100">
                            {template.title}
                          </div>
                          <div className="text-xs text-stone-500 dark:text-stone-400 line-clamp-1">
                            {template.description}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                This will replace the selected template's content with the current gem criteria.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-stone-50 dark:bg-stone-800/30 rounded-b-xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || isSubmitting}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Saving...' : mode === 'new' ? 'Create Template' : 'Overwrite Template'}
          </button>
        </div>
      </div>
    </div>
  )
}
